import { useState, type FormEvent } from 'react';
import SiteLayout from '@/components/feature/SiteLayout';
import PageHero from '@/components/feature/PageHero';
import Reveal from '@/components/base/Reveal';
import SectionHeading from '@/components/base/SectionHeading';
import { company } from '@/mocks/site';
import { services } from '@/mocks/services';
import { buildWhatsAppUrl } from '@/hooks/whatsappLink';

const FORM_ENDPOINT = 'https://readdy.ai/api/form/dblqe793ild7mhsptoqg';

const contactCards = [
  {
    icon: 'ri-map-pin-2-line',
    label: 'Atelier & siège',
    value: company.address,
    href: 'https://www.google.com/maps?q=Logbaba,+Douala,+Cameroun',
    linkLabel: 'Voir sur la carte',
  },
  {
    icon: 'ri-phone-line',
    label: 'Téléphone',
    value: company.phone,
    href: company.phoneHref,
    linkLabel: 'Appeler maintenant',
  },
  {
    icon: 'ri-mail-line',
    label: 'E-mail',
    value: company.email,
    href: `mailto:${company.email}`,
    linkLabel: 'Écrire un e-mail',
  },
  {
    icon: 'ri-time-line',
    label: 'Horaires',
    value: company.hours,
    href: '',
    linkLabel: '',
  },
];

export default function Contact() {
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [waLink, setWaLink] = useState('');

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);
    const honeypot = String(formData.get('website_alt') ?? '').trim();

    if (honeypot) {
      setStatus('success');
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === 'website_alt') return;
      const text = String(value).trim();
      if (text) payload.append(key, text);
    });

    const fullName = String(formData.get('full_name') ?? '').trim();
    const companyName = String(formData.get('company') ?? '').trim();
    const phone = String(formData.get('phone') ?? '').trim();
    const service = String(formData.get('service') ?? '').trim();
    const email = String(formData.get('email') ?? '').trim();
    const message = String(formData.get('message') ?? '').trim();

    if (!fullName) {
      setStatus('error');
      setFormError('Merci de renseigner votre nom complet.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setStatus('error');
      setFormError('Merci de saisir une adresse e-mail valide.');
      return;
    }
    if (!message) {
      setStatus('error');
      setFormError('Merci de décrire brièvement votre besoin.');
      return;
    }
    if (message.length > 500) {
      setStatus('error');
      setFormError('Merci de limiter votre message à 500 caractères.');
      return;
    }

    const whatsappUrl = buildWhatsAppUrl([
      'Nouvelle demande de devis — SGSCTIC',
      '',
      `Nom complet : ${fullName}`,
      companyName ? `Entreprise : ${companyName}` : '',
      `E-mail : ${email}`,
      phone ? `Téléphone : ${phone}` : '',
      service ? `Prestation concernée : ${service}` : '',
      `Besoin : ${message}`,
    ]);
    setWaLink(whatsappUrl);
    // Ouvert dans le geste utilisateur pour éviter le blocage des fenêtres surgissantes.
    window.open(whatsappUrl, '_blank', 'noopener,noreferrer');

    setStatus('sending');
    setFormError('');

    try {
      const response = await fetch(FORM_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMessage =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = String(serverMessage).toLowerCase().includes('spam');

      if (!response.ok || parsed?.code !== 'OK' || isSpam) {
        setStatus('error');
        setFormError(
          String(serverMessage || '').trim() ||
            "L'envoi a échoué. Merci de réessayer ou de nous appeler directement.",
        );
        return;
      }

      setStatus('success');
      form.reset();
    } catch {
      setStatus('error');
      setFormError('Une erreur réseau est survenue. Merci de réessayer ou de nous appeler.');
    }
  };

  return (
    <SiteLayout>
      <PageHero
        breadcrumb="Contact"
        label="Nous contacter"
        title="Parlons de votre besoin industriel"
        description="Une question technique, un chiffrage, une intervention urgente : notre équipe vous répond directement, sans intermédiaire."
        image="https://sgsctic.com/wp-content/uploads/2026/01/WhatsApp-Image-2025-12-19-at-10.04.28.jpeg"
        imageAlt="Équipe SGSCTIC en intervention sur un site industriel"
      />

      <section className="bg-background-50 py-16 md:py-24">
        <div className="container-wide">
          <div className="grid gap-12 lg:grid-cols-[0.85fr_1.15fr] lg:gap-16">
            {/* Left: coordinates */}
            <div>
              <Reveal>
                <SectionHeading
                  label="Coordonnées"
                  title={
                    <>
                      Un atelier à Douala,
                      <br />
                      des équipes sur le terrain
                    </>
                  }
                  description="Passez nous voir à Logbaba ou contactez-nous : nous étudions chaque demande avec attention, qu'elle concerne un ouvrage neuf ou une réparation."
                />
              </Reveal>

              <div className="mt-9 space-y-3">
                {contactCards.map((card, index) => (
                  <Reveal key={card.label} delay={index * 80}>
                    <div className="group flex items-start gap-4 rounded-lg border border-background-200 bg-background-100/70 p-5 transition-colors duration-300 hover:border-primary-300 hover:bg-background-50">
                      <span className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-md bg-primary-500 text-background-50">
                        <i className={`${card.icon} text-lg`} />
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="label-mono text-foreground-500">{card.label}</p>
                        <p className="mt-1 break-words text-sm font-semibold leading-snug text-foreground-950">
                          {card.value}
                        </p>
                        {card.href ? (
                          <a
                            href={card.href}
                            target={card.href.startsWith('http') ? '_blank' : undefined}
                            rel={card.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                            className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-primary-600 transition-colors hover:text-primary-700"
                          >
                            {card.linkLabel}
                            <i className="ri-arrow-right-up-line text-xs" />
                          </a>
                        ) : null}
                      </div>
                    </div>
                  </Reveal>
                ))}
              </div>

              <Reveal delay={360}>
                <div className="mt-6 overflow-hidden rounded-lg border border-background-200">
                  <iframe
                    title="Localisation de SGSCTIC à Logbaba, Douala"
                    src="https://www.google.com/maps?q=Logbaba,+Douala,+Cameroun&output=embed"
                    className="h-[300px] w-full border-0"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                </div>
              </Reveal>
            </div>

            {/* Right: form */}
            <Reveal delay={120}>
              <div className="rounded-lg border border-background-200 bg-background-100/60 p-6 md:p-9">
                <div className="flex items-center gap-3">
                  <span className="h-px w-8 bg-primary-500" />
                  <span className="label-mono text-primary-600">Demande de devis</span>
                </div>
                <h2 className="mt-4 font-heading text-3xl font-semibold uppercase leading-tight text-foreground-950 md:text-4xl">
                  Décrivez-nous votre projet
                </h2>
                <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                  Remplissez ce formulaire : votre demande part directement sur le WhatsApp de
                  l’atelier, et nous revenons vers vous pour préciser les contraintes techniques
                  avant de vous transmettre une proposition.
                </p>

                {status === 'success' ? (
                  <div className="mt-6 flex items-start gap-4 rounded-md border border-accent-300 bg-accent-50 p-5">
                    <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-accent-500 text-foreground-950">
                      <i className="ri-check-line" />
                    </span>
                    <div>
                      <p className="text-sm font-semibold text-accent-900">Message envoyé.</p>
                      <p className="mt-1 text-xs leading-relaxed text-accent-800">
                        Merci pour votre demande. Notre équipe vous répond dans les meilleurs
                        délais. Pour une urgence, appelez le {company.phone}.
                      </p>
                      {waLink ? (
                        <a
                          href={waLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mt-3 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-foreground-950 transition-colors hover:bg-accent-600"
                        >
                          <i className="ri-whatsapp-line text-base" />
                          Renvoyer sur WhatsApp
                        </a>
                      ) : null}
                    </div>
                  </div>
                ) : null}

                {status === 'error' && formError ? (
                  <div className="mt-6 flex items-start gap-3 rounded-md border border-primary-300 bg-primary-50 p-4">
                    <i className="ri-error-warning-line mt-0.5 text-primary-600" />
                    <p className="text-xs leading-relaxed text-primary-900">{formError}</p>
                  </div>
                ) : null}

                <form
                  id="sgsctic-devis-form"
                  data-readdy-form
                  onSubmit={handleSubmit}
                  className="mt-7 space-y-4"
                  noValidate
                >
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="min-w-0">
                      <label
                        htmlFor="full_name"
                        className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.14em] text-foreground-600"
                      >
                        Nom complet *
                      </label>
                      <input
                        id="full_name"
                        name="full_name"
                        type="text"
                        required
                        maxLength={100}
                        placeholder="Votre nom"
                        className="box-border w-full rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-500 focus:ring-2 focus:ring-primary-200"
                      />
                    </div>
                    <div className="min-w-0">
                      <label
                        htmlFor="company"
                        className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.14em] text-foreground-600"
                      >
                        Entreprise
                      </label>
                      <input
                        id="company"
                        name="company"
                        type="text"
                        maxLength={100}
                        placeholder="Société / structure"
                        className="box-border w-full rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-500 focus:ring-2 focus:ring-primary-200"
                      />
                    </div>
                  </div>

                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="min-w-0">
                      <label
                        htmlFor="email"
                        className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.14em] text-foreground-600"
                      >
                        E-mail *
                      </label>
                      <input
                        id="email"
                        name="email"
                        type="email"
                        required
                        maxLength={120}
                        placeholder="vous@entreprise.com"
                        className="box-border w-full rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-500 focus:ring-2 focus:ring-primary-200"
                      />
                    </div>
                    <div className="min-w-0">
                      <label
                        htmlFor="phone"
                        className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.14em] text-foreground-600"
                      >
                        Téléphone
                      </label>
                      <input
                        id="phone"
                        name="phone"
                        type="tel"
                        maxLength={30}
                        placeholder="+237 ..."
                        className="box-border w-full rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-500 focus:ring-2 focus:ring-primary-200"
                      />
                    </div>
                  </div>

                  <div className="relative min-w-0">
                    <label
                      htmlFor="service"
                      className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.14em] text-foreground-600"
                    >
                      Prestation concernée
                    </label>
                    <select
                      id="service"
                      name="service"
                      defaultValue=""
                      className="box-border w-full appearance-none rounded-md border border-background-300 bg-background-50 px-4 py-3 pr-10 text-sm text-foreground-950 outline-none transition-colors focus:border-primary-500 focus:ring-2 focus:ring-primary-200"
                    >
                      <option value="">Sélectionner une prestation</option>
                      {services.map((service) => (
                        <option key={service.slug} value={service.title}>
                          {service.title}
                        </option>
                      ))}
                      <option value="Autre / projet spécifique">Autre / projet spécifique</option>
                    </select>
                    <i className="ri-arrow-down-s-line pointer-events-none absolute bottom-3.5 right-3 text-lg text-foreground-500" />
                  </div>

                  <div className="min-w-0">
                    <label
                      htmlFor="message"
                      className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.14em] text-foreground-600"
                    >
                      Votre besoin *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      rows={5}
                      maxLength={500}
                      placeholder="Nature du projet, site, délais souhaités…"
                      className="box-border w-full resize-none rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-500 focus:ring-2 focus:ring-primary-200"
                    />
                    <p className="mt-1 text-[10px] uppercase tracking-[0.14em] text-foreground-400">
                      500 caractères maximum
                    </p>
                  </div>

                  <input
                    type="text"
                    name="website_alt"
                    id="website_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="form-alt-field"
                  />

                  <button
                    type="submit"
                    disabled={status === 'sending'}
                    className="btn-shine flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-4 text-sm font-semibold uppercase tracking-[0.1em] text-background-50 transition-colors duration-300 hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-70"
                  >
                    {status === 'sending' ? 'Envoi en cours…' : 'Envoyer ma demande'}
                    <i className="ri-send-plane-line text-base" />
                  </button>
                  <p className="flex items-start gap-2 text-[10px] leading-relaxed text-foreground-400">
                    <i className="ri-whatsapp-line mt-0.5 text-sm text-accent-600" />
                    <span>
                      Votre demande est enregistrée ici puis s’ouvre automatiquement dans WhatsApp
                      pour être transmise directement à l’atelier.
                    </span>
                  </p>
                </form>

                <div className="mt-5 border-t border-background-200 pt-5">
                  <a
                    href={buildWhatsAppUrl([
                      'Bonjour SGSCTIC, je souhaite obtenir un devis pour un projet industriel.',
                    ])}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-5 py-3 text-xs font-semibold uppercase tracking-[0.12em] text-foreground-800 transition-colors hover:border-accent-400 hover:bg-accent-50 hover:text-accent-900"
                  >
                    <i className="ri-whatsapp-line text-base text-accent-600" />
                    Écrire directement sur WhatsApp
                  </a>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}